/**
 * ECON-006 — Withdraw Consent trigger on Subject Detail.
 *
 * Renders a destructive-styled card when the subject has an active signed
 * consent (`active_consent_record_id` set) and is not already withdrawn.
 * Coordinator enters a reason in the inline form, then a confirmation
 * modal asks "are you sure?" before calling consent.withdraw().
 *
 * On success the subject's status flips to 'withdrawn' server-side and the
 * post-withdrawal activity gate (visits, eCRF) starts refusing writes.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, ShieldOff } from 'lucide-react';
import { consent, type ConsentRecordRow, type SubjectStatus } from '@/api/client';
import { errorText } from '@/lib/permissionError';

interface Props {
  subjectId: string;
  status: SubjectStatus;
  activeConsentRecordId?: string | null;
}

export function WithdrawConsentTrigger({ subjectId, status, activeConsentRecordId }: Props) {
  const qc = useQueryClient();

  // If the parent didn't pass the active_consent_record_id (older detail
  // responses), fall back to the most recent signed-and-not-withdrawn
  // consent record so the panel still renders for valid subjects.
  const recordsQuery = useQuery({
    queryKey: ['consent', 'records', subjectId],
    queryFn: () => consent.listRecordsForSubject(subjectId),
    enabled: !activeConsentRecordId && status !== 'withdrawn' && !!subjectId,
  });

  const fallbackRecordId = useMemo<string | null>(() => {
    const rows = recordsQuery.data?.records ?? [];
    const signedActive = rows.find(
      (r: ConsentRecordRow) =>
        !!r.signed_at && !(r as unknown as { withdrawn_at?: string | null }).withdrawn_at,
    );
    return signedActive?.consent_record_id ?? null;
  }, [recordsQuery.data]);

  const effectiveRecordId = activeConsentRecordId ?? fallbackRecordId;

  const [reason, setReason] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const withdraw = useMutation({
    mutationFn: () => consent.withdraw(effectiveRecordId!, { reason, scope: 'coordinator_initiated' }),
    onSuccess: () => {
      toast.success('Consent withdrawn. Subject is now marked withdrawn.');
      setConfirmOpen(false);
      setFormOpen(false);
      setReason('');
      qc.invalidateQueries({ queryKey: ['subject', subjectId] });
      qc.invalidateQueries({ queryKey: ['consent', 'records', subjectId] });
    },
    onError: (err) => {
      toast.error(errorText(err, 'Withdrawal failed.'));
    },
  });

  // Only a subject still on the lifecycle can withdraw: not one who already
  // left (Withdrawn, Screen Fail) or finished (Completed). Same rule as the
  // server since 16 Oct 2026.
  if (status === 'withdrawn' || status === 'screen_fail' || status === 'completed') return null;
  if (!effectiveRecordId) return null;

  const reasonTooShort = reason.trim().length < 3;

  return (
    <section className="rounded border border-danger-300 bg-danger-50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-danger-900">
            <ShieldOff className="h-4 w-4" />
            Withdraw consent
          </h2>
          <p className="mt-1 text-xs text-danger-800">
            Records a post-signature withdrawal. The subject is flipped to <span className="font-semibold">withdrawn</span>{' '}
            and every visit that has not happened yet is cancelled. No new visits or forms can be started; data
            already recorded can still be completed and corrected. The signed consent PDF remains on file for audit.
          </p>
        </div>
        {!formOpen && (
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="shrink-0 rounded border border-danger-400 bg-white px-3 py-1 text-xs font-semibold text-danger-800 hover:bg-danger-100"
          >
            Withdraw consent
          </button>
        )}
      </div>

      {formOpen && (
        <div className="mt-3 space-y-3 border-t border-danger-300 pt-3">
          <label className="block text-xs">
            <span className="font-medium text-danger-900">
              Reason for withdrawal <span className="text-red-600">*</span>
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              minLength={3}
              maxLength={2000}
              rows={3}
              placeholder="Subject called the site and asked to withdraw…"
              className="mt-1 w-full rounded border border-danger-300 bg-white px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted"
            />
            <span className="mt-1 block text-[10px] text-danger-800">3–2000 characters.</span>
          </label>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setFormOpen(false);
                setReason('');
              }}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={reasonTooShort}
              onClick={() => setConfirmOpen(true)}
              className="btn bg-danger-500 text-white shadow-card hover:bg-danger-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Confirm withdrawal
            </button>
          </div>
        </div>
      )}

      {confirmOpen && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="withdraw-confirm-title"
        >
          <div className="card w-full max-w-md space-y-4 p-6">
            <h3 id="withdraw-confirm-title" className="text-lg font-semibold text-danger-800">
              Are you sure?
            </h3>
            <p className="text-sm text-ink-secondary">
              Withdrawing consent flips this subject to <span className="font-semibold">withdrawn</span> and blocks
              all subsequent study activity. This action is logged in the audit trail and cannot be undone from this
              screen.
            </p>
            <div className="rounded border border-surface-border bg-surface-muted/40 p-2 text-xs text-ink-muted">
              <span className="font-medium text-ink-secondary">Reason:</span>{' '}
              <span className="italic">{reason}</span>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={withdraw.isPending}
                className="btn btn-secondary disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => withdraw.mutate()}
                disabled={withdraw.isPending}
                className="btn bg-danger-500 text-white shadow-card hover:bg-danger-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {withdraw.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                Yes, withdraw consent
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
